import { Suspense, type ComponentType } from "react";
import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
} from "react-router-dom";
import { ProtectedShell } from "@/components/layout/ProtectedShell";
import { RouteLoading } from "@/components/feedback/RouteLoading";
import { ErrorBoundary } from "@/components/feedback/ErrorBoundary";
import { TelemetryRouteTracker } from "@/components/feedback/TelemetryRouteTracker";
import {
  FALLBACK_ROUTE,
  PROTECTED_ROUTES,
  PUBLIC_ROUTES,
  type RouteSpec,
} from "@/config/routes";

function wrap(Component: ComponentType<unknown>) {
  return (
    <Suspense fallback={<RouteLoading />}>
      <ErrorBoundary>
        <Component />
      </ErrorBoundary>
    </Suspense>
  );
}

function toRouteObject(spec: RouteSpec): RouteObject {
  const route: RouteObject = {
    path: spec.path,
    element: wrap(spec.Component as ComponentType<unknown>),
  };
  if (spec.handle) route.handle = spec.handle;
  if (spec.errorElement) route.errorElement = spec.errorElement;
  if (spec.children?.length) route.children = spec.children.map(toRouteObject);
  return route;
}

export function buildRouter(opts: {
  publicRoutes: RouteSpec[];
  protectedRoutes: RouteSpec[];
  fallback: RouteSpec;
}) {
  return createBrowserRouter([
    {
      element: <TelemetryRouteTracker />,
      children: [
        ...opts.publicRoutes.map(toRouteObject),
        {
          element: <ProtectedShell />,
          children: [
            { index: true, element: <Navigate to="/dashboard" replace /> },
            ...opts.protectedRoutes.map(toRouteObject),
          ],
        },
        toRouteObject(opts.fallback),
      ],
    },
  ]);
}

export const router = buildRouter({
  publicRoutes: PUBLIC_ROUTES,
  protectedRoutes: PROTECTED_ROUTES,
  fallback: FALLBACK_ROUTE,
});
